import React from 'react';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import Badge from 'four-nets/ui-v2/badges/badge/badge';
import PanelPosts from 'four-nets/photoblog/panels/panel-posts';

import query, { FeaturedContentQuery } from './featured-content.graphql';
import defaultStyles from './featured-content.scss';

export interface Props {
    styles: typeof defaultStyles;
}

const FearutedContent: React.FC<Props> = props => {
    const { styles } = props;
    const { data } = useQuery<FeaturedContentQuery>(query);

    // Pretty ugly combined ordering of Threads & Articles
    const objects = [];
    if (data && data.featuredContent && data.featuredContent.threads) {
        objects.push(
            ...data.featuredContent.threads
                .filter(Boolean)
                .map(thread => [thread!.id, new Date(thread!.lastMessage!.time!)])
        );
    }
    if (data && data.featuredContent && data.featuredContent.articles) {
        objects.push(
            ...data.featuredContent.articles
                .filter(Boolean)
                .map(article => [article!.id, new Date(article!.lastChangeTime!)])
        );
    }
    objects.sort((o1, o2) => (o1[1] < o2[1] ? 1 : -1));
    const idToOrder = new Map(objects.map((value, index) => [value[0], index + 1]));

    return data && data.featuredContent ? (
        <div className={styles.wrapper}>
            <h2 className={styles.heading}>{data.featuredContent.title}</h2>
            <div className={styles.featuredContent}>
                {data.featuredContent.threads || data.featuredContent.articles ? (
                    <>
                        <div className={styles.subheading}>
                            <FormattedMessage id='FEATURED_CONTENT_TITLE1' />
                        </div>
                        <ul className={styles.group}>
                            {data.featuredContent.threads &&
                                data.featuredContent.threads.map(thread => (
                                    <li
                                        className={styles.item}
                                        key={thread!.id}
                                        style={{ order: idToOrder.get(thread!.id) }}
                                    >
                                        <Link
                                            to={`/forum/${thread!.category!.slug}/${thread!.slug}`}
                                            className={styles.link}
                                        >
                                            {thread!.title}
                                        </Link>
                                        <Badge uppercase={true}>
                                            <FormattedMessage id='BADGE_DISCUSSION' />
                                        </Badge>
                                    </li>
                                ))}
                            {data.featuredContent.articles &&
                                data.featuredContent.articles.map(article => (
                                    <li
                                        className={styles.item}
                                        key={article!.id}
                                        style={{ order: idToOrder.get(article!.id) }}
                                    >
                                        <Link to={article!.href} className={styles.link}>
                                            {article!.title}
                                        </Link>
                                        <Badge uppercase={true}>
                                            <FormattedMessage id='ARTICLE' />
                                        </Badge>
                                    </li>
                                ))}
                        </ul>
                    </>
                ) : null}
                {data.featuredContent.albums ? (
                    <>
                        <div className={styles.subheading}>
                            <FormattedMessage id='FEATURED_CONTENT_TITLE2' />
                        </div>

                        <PanelPosts
                            style={{ row: styles.panelPostsRow, panel: styles.panelPostsPanel }}
                            edges={data.featuredContent.albums.map(album => ({ node: album })) as any}
                        />
                    </>
                ) : null}

                {data.featuredContent.groups ? (
                    <>
                        <div className={styles.subheading}>
                            <FormattedMessage id='FEATURED_CONTENT_TITLE3' />
                        </div>
                        <ul className={classnames(styles.group, styles.noBorder)}>
                            {data.featuredContent.groups.map(group => (
                                <li className={styles.item} key={group!.id}>
                                    <Link to={group!.href} className={styles.link}>
                                        {group!.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </>
                ) : null}
            </div>
        </div>
    ) : null;
};

export default Styled(defaultStyles)(FearutedContent);
